import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import { useState, useContext, useEffect, ChangeEvent } from 'react'
import PrimaryButton from '../../../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../../../global/components/buttons/secondary-button/SecondaryButton'
import styles from '../styles/styles.module.css'
import { useParams } from 'react-router-dom'
import { fetchData } from '../../../../../../../global/utils/fetch'
import { WoDetailContext } from '../../../../../context/context'
import { ToastNotificationContext } from '../../../../../../../global/context/toast-context/ToastNotificationContext'
import {
  determineEffectOfTechnicianLock,
  getAvailableTechniciansForOrderLock,
  removeLockedTechnicianFromOrder,
} from './api'
import { ITechnician } from '../../../../../../../../../app/entities/Technician'

export default function EditLockedTechnician({
  handleClose,
}: {
  handleClose: Function
}) {
  const { id: orderObjectId } = useParams()
  const { queryOrderData, data: orderData } = useContext(WoDetailContext)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTechnician, setSelectedTechnician] =
    useState<ITechnician | null>(orderData.lockedTechnician || null)
  const [availableTechnicians, setAvailableTechnicians] = useState<
    ITechnician[]
  >([])
  const [isChecked, setIsChecked] = useState(false)

  const hasLockedTechnician = Boolean(orderData.lockedTechnician)

  useEffect(() => {
    async function fetchAvailableTechnicians() {
      try {
        setIsLoading(true)
        const technicians = await fetchData<ITechnician[]>(
          getAvailableTechniciansForOrderLock(orderObjectId || ''),
        )
        setAvailableTechnicians(technicians)
      } catch (err: any) {
        console.error(err.message)
        setToastStatus({
          ...toastStatus,
          isOpen: true,
          message:
            'An error occurred while fetching available technicians. Please try again.',
          severity: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    }

    if (orderObjectId) {
      fetchAvailableTechnicians()
    }
  }, [orderObjectId])

  async function handleRemoveTechnician() {
    try {
      setIsLoading(true)
      await fetchData<boolean>(
        removeLockedTechnicianFromOrder(orderObjectId || ''),
      )
      await queryOrderData(orderObjectId)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Technician unlocked from order successfully.',
        severity: 'success',
      })
      setIsLoading(false)
      handleClose()
    } catch (err: any) {
      console.error(err.message)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message:
          'An error occurred while unlocking the technician. Please try again.',
        severity: 'error',
      })
      setIsLoading(false)
    }
  }

  async function handleLockTechnician() {
    try {
      setIsLoading(true)
      const needToConfirmChange = await fetchData<boolean>(
        determineEffectOfTechnicianLock(
          orderObjectId || '',
          selectedTechnician?.objectId || '',
        ),
      )
      if (needToConfirmChange) {
        if (
          !window.confirm(
            [
              'This technician cannot be set to this order without',
              'affecting the schedule. Continuing will bump this order',
              'to pending reschedule. Are you sure you want to continue?',
            ].join(' '),
          )
        ) {
          return
        } else {
          //we will force the lock and bump to pending reschedule
          await fetchData<boolean>(
            determineEffectOfTechnicianLock(
              orderObjectId || '',
              selectedTechnician?.objectId || '',
              true,
            ),
          )
        }
      }
      await queryOrderData(orderObjectId)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Technician locked to order successfully.',
        severity: 'success',
      })
      setIsLoading(false)
      handleClose()
    } catch (err: any) {
      console.error(err.message)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message:
          'An error occurred while locking the technician. Please try again.',
        severity: 'error',
      })
      setIsLoading(false)
    }
  }

  function handleTechnicianChange(event: ChangeEvent<HTMLInputElement>) {
    const technicianObjectId = event.target.value
    const technician =
      availableTechnicians.find((t) => t.objectId === technicianObjectId) ||
      null
    setSelectedTechnician(technician)
  }

  return (
    <div className={styles.inputsContainer}>
      {isLoading && (
        <div className={styles.progressContainer}>
          <CircularProgress />
        </div>
      )}
      <TextField
        label='Select Technician'
        select
        fullWidth
        value={selectedTechnician?.objectId || ''}
        onChange={handleTechnicianChange}
        disabled={isLoading || availableTechnicians.length === 0 || isChecked}
        SelectProps={{
          MenuProps: {
            PaperProps: {
              style: {
                maxHeight: 400,
              },
            },
          },
        }}
      >
        <MenuItem
          value=''
          disabled
        >
          {isLoading
            ? 'Loading technicians...'
            : availableTechnicians.length === 0
            ? 'No available technicians'
            : 'Select a technician'}
        </MenuItem>
        {availableTechnicians.map((technician) => (
          <MenuItem
            key={technician.objectId}
            value={technician.objectId}
          >
            {technician.name}
          </MenuItem>
        ))}
      </TextField>
      {hasLockedTechnician && (
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              disabled={isLoading}
            />
          }
          label='Remove locked technician from order'
        />
      )}
      <div className={styles.buttonContainer}>
        <SecondaryButton
          buttonName='Cancel'
          onClick={() => handleClose()}
        />
        <PrimaryButton
          buttonName={isChecked ? 'Unlock' : 'Lock'}
          disabled={isChecked ? isLoading : !selectedTechnician || isLoading}
          onClick={isChecked ? handleRemoveTechnician : handleLockTechnician}
        />
      </div>
    </div>
  )
}
